import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser, authKeys } from '@/api/auth';
import { AUTH_LOGOUT_EVENT } from '@/lib/http';
import { tokenStorage, type AuthMethod, type Impersonator } from '@vnetwork/margarita-core/auth';
import { verifyImpersonation } from '@/api/impersonation';
import { identifyUser, trackLogin, trackLogout } from '@/lib/analytics-events';
import { setSentryUser } from '@/lib/sentry';
import { endKeycloakSession } from '@/lib/sso';
import type { SessionTokens } from '@/api/auth-flow';
import type { User } from '@/auth/types';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** True when the session is valid but the account no longer maps to a live org
   * contact (removed member) — the app shows an "access removed" screen. */
  isContactRevoked: boolean;
  /** Persist a freshly issued session, then refetch the current user. */
  setSession: (tokens: SessionTokens, method?: AuthMethod) => boolean;
  logout: () => void;
  /** True when a DC employee is impersonating this customer (vs a direct login). */
  isImpersonating: boolean;
  /** The DC employee behind the impersonation, or null for a direct login. */
  impersonatedBy: Impersonator | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  // Reactive mirror of "is there a token?" — flipping this re-enables the
  // current-user query immediately after login (a bare localStorage read isn't
  // reactive and would leave the query disabled until a full reload).
  const [hasToken, setHasToken] = useState(() => Boolean(tokenStorage.access));
  // Impersonation is established once at bootstrap (ImpersonationPage does a hard
  // reload after writing the session), so the initial read is stable for the
  // session; logout does a full navigation which resets this. The plaintext
  // fallback shows an instant banner; the verify effect below confirms it
  // server-side and either replaces it with the trusted identity or clears it.
  const [impersonatedBy, setImpersonatedBy] = useState<Impersonator | null>(
    () => tokenStorage.impersonatedBy,
  );

  const { data: user, isLoading, isError } = useCurrentUser(hasToken);

  const setSession = useCallback(
    (tokens: SessionTokens, method: AuthMethod = 'local'): boolean => {
      const { access_token, refresh_token, exp, uuid } = tokens;
      if (!access_token || !exp || !uuid) return false;
      tokenStorage.set({
        access: access_token,
        refresh: refresh_token,
        expSeconds: exp,
        uuid,
        authMethod: method,
      });
      queryClient.invalidateQueries({ queryKey: authKeys.currentUser });
      setHasToken(true);
      // Central login funnel point — every fresh session (local/SSO) lands here.
      trackLogin(method);
      return true;
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    trackLogout();
    setSentryUser(null);
    // Keycloak-issued sessions ('sso' + standalone 'google') also hold a
    // server-side SSO session on the Keycloak host — end it so the next login
    // shows the account chooser instead of silently reusing the old user.
    // Must read before clear(); fire-and-forget (keepalive survives the
    // navigation below).
    const refresh = tokenStorage.refresh;
    if (refresh && tokenStorage.authMethod !== 'local') {
      endKeycloakSession(refresh);
    }
    tokenStorage.clear();
    queryClient.removeQueries({ queryKey: authKeys.currentUser });
    setHasToken(false);
    window.location.assign('/auth/login');
  }, [queryClient]);

  // React to forced logouts raised by the API layer (refresh failure / 401).
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener(AUTH_LOGOUT_EVENT, handler);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handler);
  }, [logout]);

  // Identify the signed-in user once the current-user query resolves — to GA4
  // (user_id + coarse props, no PII) and to Sentry (id + email + name, so error
  // events carry the user, not just the IP). Covers all login paths + reload.
  useEffect(() => {
    if (user?.uuid) {
      identifyUser(user);
      setSentryUser(user);
    }
  }, [user?.uuid, user?.organizationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Confirm the impersonation server-side: @mgmt decrypts the token DataCentral
  // minted. A valid token replaces the plaintext fallback with the trusted
  // identity; an invalid/expired/foreign one clears the banner. Network errors
  // keep the non-authoritative plaintext so the session still reads as staff-driven.
  useEffect(() => {
    const token = tokenStorage.impersonationToken;
    if (!token) return;
    let cancelled = false;
    verifyImpersonation(token)
      .then((res) => {
        if (cancelled) return;
        if (res.valid && res.impersonator) {
          setImpersonatedBy({
            id: String(res.impersonator.id),
            name: res.impersonator.name,
            email: res.impersonator.email,
          });
        } else {
          setImpersonatedBy(null);
        }
      })
      .catch(() => {
        /* keep the plaintext fallback banner on a transient verify failure */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: user ?? null,
      isLoading: hasToken && isLoading,
      isAuthenticated: Boolean(user) && !isError,
      isContactRevoked: Boolean(user?.contactRevoked),
      setSession,
      logout,
      isImpersonating: Boolean(impersonatedBy),
      impersonatedBy,
    }),
    [user, isLoading, isError, hasToken, setSession, logout, impersonatedBy],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
